import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { BOOKING_DURATION_MINUTES, BOOKING_URL } from '../../config/contact';

export const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  const isActive = (path: string) => location.pathname === path;
  
  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/solutions', label: 'Solutions' },
    { path: '/contact', label: 'Contact' },
  ];
  
  return (
    <header className="sticky top-0 z-50 border-b border-gray-200 bg-white/95 backdrop-blur">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-[76px] items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="/logo.svg" 
                alt="Appeal Advantage Logo" 
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold tracking-tight">
                <span className="text-brand-blue">Appeal</span>
                <span className="text-brand-red"> Advantage</span>
              </span>
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden items-center space-x-7 md:flex">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-brand-red'
                    : 'text-brand-blue hover:text-brand-red'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            <div className="flex items-center space-x-4">
              <a
                href="https://app.appealadvantage.com/login"
                className="text-sm font-medium text-brand-blue transition-colors hover:text-brand-red"
              >
                Sign In
              </a>
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-lg bg-brand-red px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-red-600"
              >
                Book a review
              </a>
            </div>
          </div>
          
          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          >
            {mobileMenuOpen ? (
              <XMarkIcon className="w-6 h-6 text-brand-blue" />
            ) : (
              <Bars3Icon className="w-6 h-6 text-brand-blue" />
            )}
          </button>
        </div>
      </nav>
      
      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 py-4">
          <div className="space-y-1 px-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-2 font-medium ${
                  isActive(link.path)
                    ? 'text-brand-red'
                    : 'text-brand-blue hover:text-brand-red'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href="https://app.appealadvantage.com/login"
              className="block py-2 text-brand-blue hover:text-brand-red font-medium"
            >
              Sign In
            </a>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileMenuOpen(false)}
              className="mt-4 flex w-full items-center justify-center rounded-lg bg-brand-red px-6 py-3 font-semibold text-white transition-colors hover:bg-brand-red-600"
            >
              Book a {BOOKING_DURATION_MINUTES}-minute workflow review
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
